import { InfoOutlined, EditOutlined, CheckCircle } from '@mui/icons-material';
import { Button, CardContainer, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Input } from '@/components/common/Input';
import {
  useCreditSettings,
  useCreditSettingsStatus,
  useUpdateCreditSettings,
} from '@/hooks/queries/useOrganizationConfig';
import type { RatePeriod } from '@/services/organizationConfig.service';

export const CreditSettingsCard: React.FC = () => {
  const { data: creditSettings, isLoading } = useCreditSettings();
  const { data: status } = useCreditSettingsStatus();
  const updateCreditSettings = useUpdateCreditSettings();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    creditInterestRate: 0,
    period: 'daily' as RatePeriod,
    creditTermDays: 0,
    creditMaxAmount: null as number | null,
    creditMaxSalaryPercentage: 0,
  });

  const periodOptions: DropdownOption[] = [
    { value: 'daily', label: 'Diario' },
    { value: 'yearly', label: 'Anual' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'hourly', label: 'Por Hora' },
  ];

  const translatePeriod = (period: string): string => {
    const translations: Record<string, string> = {
      hourly: 'por hora',
      daily: 'diario',
      weekly: 'semanal',
      monthly: 'mensual',
      yearly: 'anual',
    };
    return translations[period] || period;
  };

  const handleEdit = () => {
    if (creditSettings) {
      setFormData({
        creditInterestRate: creditSettings.creditInterestRate,
        period: creditSettings.period,
        creditTermDays: creditSettings.creditTermDays,
        creditMaxAmount: creditSettings.creditMaxAmount,
        creditMaxSalaryPercentage: creditSettings.creditMaxSalaryPercentage,
      });
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.creditInterestRate < 0 || formData.creditInterestRate > 100) {
      toast.error('La tasa de interés debe estar entre 0% y 100%');
      return;
    }
    if (formData.creditTermDays < 1 || formData.creditTermDays > 365) {
      toast.error('El plazo debe estar entre 1 y 365 días');
      return;
    }
    if (formData.creditMaxSalaryPercentage < 0 || formData.creditMaxSalaryPercentage > 100) {
      toast.error('El porcentaje de salario debe estar entre 0% y 100%');
      return;
    }
    if (formData.creditMaxAmount !== null && formData.creditMaxAmount < 0) {
      toast.error('El monto máximo no puede ser negativo');
      return;
    }

    try {
      await updateCreditSettings.mutateAsync(formData);
      toast.success('Cambio enviado a revisión. Un super admin debe aprobarlo antes de aplicarse.');
      setIsEditing(false);
    } catch (_error) {
      toast.error('Error al actualizar la configuración');
    }
  };

  if (isLoading) {
    return (
      <CardContainer>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/3"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
            <div className="h-4 bg-white/10 rounded w-1/2"></div>
          </div>
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white">Configuración de Créditos</h3>
            <p className="text-sm text-muted mt-1">
              Define los parámetros para adelantos de nómina
            </p>
          </div>
          {!isEditing && (
            <Button variant="white" size="small" onClick={handleEdit}>
              <EditOutlined fontSize="small" className="mr-1" />
              Editar
            </Button>
          )}
        </div>

        {/* Status Warnings */}
        {status && !status.isConfigured && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <InfoOutlined className="text-yellow-400 mt-0.5" fontSize="small" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-300">Configuración incompleta</p>
                {status.missingFields.length > 0 && (
                  <p className="text-xs text-yellow-400 mt-1">
                    Campos faltantes: {status.missingFields.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {isEditing ? (
          <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  id="creditInterestRate"
                  label="Tasa de Interés (%)"
                  type="number"
                  value={formData.creditInterestRate}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      creditInterestRate: Number(e.target.value),
                    }))
                  }
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="0.57"
                  disabled={updateCreditSettings.isPending}
                />
                <p className="text-xs text-muted mt-1">Valor por período (ej. 0.57 diario)</p>
              </div>

              <div>
                <label
                  htmlFor="ratePeriod"
                  className="block text-sm font-medium text-brand-white mb-1"
                >
                  Período
                </label>
                <Dropdown
                  id="ratePeriod"
                  value={formData.period}
                  onChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      period: String(value) as RatePeriod,
                    }))
                  }
                  options={periodOptions}
                  disabled={updateCreditSettings.isPending}
                />
              </div>
            </div>

            <div>
              <Input
                id="creditTermDays"
                label="Plazo de Crédito (días)"
                type="number"
                value={formData.creditTermDays}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    creditTermDays: Number(e.target.value),
                  }))
                }
                min="1"
                max="365"
                placeholder="15"
                disabled={updateCreditSettings.isPending}
              />
              <p className="text-xs text-muted mt-1">Rango: 1 - 365 días</p>
            </div>

            <div>
              <Input
                id="creditMaxAmount"
                label="Monto Máximo de Crédito (opcional)"
                type="number"
                value={formData.creditMaxAmount || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    creditMaxAmount: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                min="0"
                placeholder="100000"
                disabled={updateCreditSettings.isPending}
              />
              <p className="text-xs text-muted mt-1">Deja vacío para sin límite</p>
            </div>

            <div>
              <Input
                id="creditMaxSalaryPercentage"
                label="Porcentaje Máximo de Salario (%)"
                type="number"
                value={formData.creditMaxSalaryPercentage}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    creditMaxSalaryPercentage: Number(e.target.value),
                  }))
                }
                step="1"
                min="0"
                max="100"
                placeholder="50"
                disabled={updateCreditSettings.isPending}
              />
              <p className="text-xs text-muted mt-1">
                Porcentaje del salario que puede adelantarse
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="submit"
                variant="black"
                size="small"
                disabled={updateCreditSettings.isPending}
              >
                {updateCreditSettings.isPending ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
              <Button
                type="button"
                variant="white"
                size="small"
                onClick={handleCancel}
                disabled={updateCreditSettings.isPending}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-white/5 rounded-lg">
              {status?.hasRates ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-400" fontSize="small" />
                  <p className="text-sm text-brand-white">
                    Tasa configurada:{' '}
                    <span className="font-semibold">
                      {creditSettings?.creditInterestRate}%{' '}
                      {translatePeriod(creditSettings?.period ?? 'daily')}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <InfoOutlined className="text-yellow-400" fontSize="small" />
                  <p className="text-sm text-muted">
                    Usando tasa predeterminada del sistema:{' '}
                    {creditSettings?.creditInterestRate}%{' '}
                    {translatePeriod(creditSettings?.period ?? 'daily')}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted">Tasa de Interés</p>
                <p className="text-lg font-semibold text-brand-white">
                  {creditSettings?.creditInterestRate}%
                </p>
                <p className="text-xs text-muted">
                  {translatePeriod(creditSettings?.period ?? 'daily')}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted">Plazo de Crédito</p>
                <p className="text-lg font-semibold text-brand-white">
                  {creditSettings?.creditTermDays} días
                </p>
              </div>

              <div>
                <p className="text-sm text-muted">Monto Máximo</p>
                <p className="text-lg font-semibold text-brand-white">
                  {creditSettings?.creditMaxAmount
                    ? `$${creditSettings.creditMaxAmount.toLocaleString()}`
                    : 'Sin límite'}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted">% Máximo de Salario</p>
                <p className="text-lg font-semibold text-brand-white">
                  {creditSettings?.creditMaxSalaryPercentage}%
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
